import React, { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProtectedDevRoute } from './components/ProtectedDevRoute';
import Login from './Login';
import DebugPage from './DebugPage';
import SearchPage from './SearchPage';
import ApprovalPage from './ApprovalPage';
import AdminPage from './AdminPage';
import ReportsPage from './ReportsPage';

// ── Role helper — reads cdaRole set by the backend on /api/auth/me ────────────
// Roles: 'admin' | 'user'
// DB-listed internal users receive 'admin' from the backend (no separate 'approver' role).
function useRole() {
  const { user } = useAuth();
  const role = user?.cdaRole || 'user';
  return {
    role,
    isAdmin:    role === 'admin',
    isApprover: role === 'admin',
    isUser:     true,
  };
}

// ── Inner app — rendered inside AuthProvider ──────────────────────────────────

function MainApp() {
  const { user, logout } = useAuth();
  const { isAdmin, isApprover } = useRole();
  const [currentPage, setCurrentPage] = useState('search');

  const safePage = (page) => {
    if (page === 'admin'     && !isAdmin)    return 'search';
    if (page === 'approvals' && !isApprover) return 'search';
    if (page === 'reports'   && !isApprover) return 'search';
    return page;
  };

  const navigate = (page) => setCurrentPage(safePage(page));

  const renderPage = () => {
    switch (currentPage) {
      case 'search':    return <SearchPage user={user} isAdmin={isAdmin} />;
      case 'approvals': return <ApprovalPage user={user} />;
      case 'admin':     return <AdminPage user={user} />;
      case 'reports':   return <ReportsPage user={user} isAdmin={isApprover} />;
      default:          return <SearchPage user={user} isAdmin={isAdmin} />;
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <div className="logo-container">
            <img src="/broadcom-logo.jpg" alt="Broadcom" className="header-logo" />
          </div>
          <h1>Code Disk/Strip Application</h1>
        </div>
        <div className="header-right">
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
      </header>

      <nav className="navigation">
        <button
          className={currentPage === 'search' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => navigate('search')}
        >
          Search
        </button>
        {isApprover && (
          <button
            className={currentPage === 'reports' ? 'nav-btn active' : 'nav-btn'}
            onClick={() => navigate('reports')}
          >
            Reports
          </button>
        )}

        {isApprover && (
          <button
            className={currentPage === 'approvals' ? 'nav-btn active' : 'nav-btn'}
            onClick={() => navigate('approvals')}
          >
            Approvals
          </button>
        )}

        {isAdmin && (
          <button
            className={currentPage === 'admin' ? 'nav-btn active' : 'nav-btn'}
            onClick={() => navigate('admin')}
          >
            Admin
          </button>
        )}
      </nav>

      <div className="main-content">
        {renderPage()}
      </div>

      <footer className="App-footer">
        <p>© 2026 Broadcom. All Rights Reserved.</p>
        <p>
          Need help?{' '}
          <a
            href={process.env.REACT_APP_SUPPORT_PORTAL_URL || 'https://support.broadcom.com/'}
            target="_blank"
            rel="noopener noreferrer"
            className="support-link"
          >
            Visit the Broadcom Support Portal
          </a>
        </p>
      </footer>
    </div>
  );
}

// ── Root app — router + auth provider ─────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/debug"
            element={
              <ProtectedDevRoute>
                <DebugPage />
              </ProtectedDevRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
